import { z } from "zod";

/**
 * Reports validation (Phase 5.6).
 *
 * Boundary validation for report generation and template persistence. Mirrors
 * the legacy `web/js/reports.js` payload: a system id, a company identifier, and
 * an ordered list of field keys.
 */

/** POST /api/v1/reports/generate body. */
export const reportGenerateSchema = z.object({
  system: z.string().trim().min(1, "a system is required").max(200),
  company: z.string().trim().min(1, "a company is required").max(200),
  fields: z
    .array(z.string().trim().min(1).max(200))
    .min(1, "select at least one field")
    .max(200),
});

export type ReportGenerateInput = z.infer<typeof reportGenerateSchema>;

/**
 * POST /api/v1/reports/export body — the same selection as generate, plus a
 * file format.
 *
 * Export is a SERVER-SIDE operation on purpose. Serializing rows in the browser
 * would leave the capability ungoverned, because the data has already been
 * handed over by then; frontend behaviour is never an authorization boundary. A
 * separate endpoint is what makes "may generate but may not export" a real,
 * enforceable distinction rather than a hidden button.
 */
export const reportExportSchema = reportGenerateSchema.extend({
  format: z.enum(["csv", "json"]),
});

export type ReportExportInput = z.infer<typeof reportExportSchema>;

/** POST /api/v1/reports/templates body. */
export const createReportTemplateSchema = z.object({
  name: z.string().trim().min(1, "a template name is required").max(120),
  systemId: z.string().trim().min(1).max(200),
  fields: z
    .array(z.string().trim().min(1).max(200))
    .min(1, "a template needs at least one field")
    .max(200),
});

export type CreateReportTemplateInput = z.infer<
  typeof createReportTemplateSchema
>;
